import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useApp } from "@/contexts/AppContext";

interface StripeTerminalConnectionProps {
  showTitle?: boolean;
  showDisconnect?: boolean;
}

export function StripeTerminalConnection({
  showTitle = true,
  showDisconnect = true,
}: StripeTerminalConnectionProps) {
  const {
    locationId,
    isInitializing,
    initError,
    isDiscovering,
    isConnecting,
    readers,
    selectedReaderId,
    setSelectedReaderId,
    connectedReader,
    discoverReaders,
    connectReader,
    disconnectReader,
  } = useApp();

  return (
    <div className="space-y-3">
      {showTitle ? <h4 className="font-medium">Reader connection</h4> : null}

      {isInitializing ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Initializing Stripe Terminal...
        </div>
      ) : initError ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
          {initError}
        </div>
      ) : connectedReader ? (
        <div className="space-y-3">
          <p className="text-sm text-green-600">Connected to {connectedReader.label}</p>
          {showDisconnect ? (
            <Button variant="outline" onClick={disconnectReader}>
              Disconnect reader
            </Button>
          ) : null}
        </div>
      ) : !readers.length ? (
        <div className="space-y-3">
          {!locationId && (
            <p className="text-sm text-muted-foreground">
              No Terminal location configured — using simulated readers for testing. Configure a
              location in{" "}
              <Link to="/settings/integrations/stripe" className="underline">
                Settings → Stripe
              </Link>
              .
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            Discover nearby Stripe readers to collect in-person payments.
          </p>
          <Button onClick={discoverReaders} disabled={isDiscovering || isConnecting}>
            {isDiscovering ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Discovering...
              </>
            ) : (
              "Discover Readers"
            )}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Available readers</label>
            <select
              className="mt-1 w-full rounded border p-2 text-sm"
              value={selectedReaderId}
              onChange={(e) => setSelectedReaderId(e.target.value)}
              disabled={isConnecting}
            >
              <option value="">Select a reader</option>
              {readers.map((reader) => (
                <option key={reader.id} value={reader.id}>
                  {reader.label} ({reader.status})
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <Button onClick={connectReader} disabled={!selectedReaderId || isConnecting}>
              {isConnecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                "Connect Reader"
              )}
            </Button>
            <Button
              variant="outline"
              onClick={discoverReaders}
              disabled={isDiscovering || isConnecting}
            >
              {isDiscovering ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh readers"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
